import type { Routes } from '@angular/router';

/**
 * El mapa de rutas de la Consola v2 — AD-14, Historia 1.5b.
 *
 * La ruta canónica es en español (decisión del dueño del producto, 2026-09-01) y **el renombre
 * sigue al nav**: se renombra lo que el nav de 22 destinos nombra, y nada más. Cada ruta vieja
 * queda como **redirección permanente**. «Permanente» en una SPA no es un 301 de HTTP: es que la
 * entrada **no se saca nunca** de la tabla. No hay fecha de vencimiento y no hay limpieza futura,
 * porque el que la usa es un marcador guardado hace un año, no un enlace del producto.
 *
 * Por qué esto importa más que la prolijidad. La tabla termina en
 * `{ path: '**', redirectTo: '/sign-in' }` (`app.routes.ts`), así que una ruta que deja de existir
 * **no da 404: manda al login**. El usuario que abre su marcador de `/dashboard` no lee «enlace
 * roto», lee «se me cayó la sesión», y el ticket que abre manda al soporte a buscar el bug en
 * autenticación. La redirección es lo único que separa un renombre de una expulsión.
 *
 * La aritmética, para que se pueda contar sin leer todo el archivo. Del shell cuelgan **21**
 * rutas hijas (`app.routes.ts`, el bloque de `children`), de las cuales **3 ya estaban en
 * español** —`calendario`, `recursos`, `catalogo`—. Quedan **18 en inglés**, que es el número que
 * cuenta la Historia 1.5b. Esas 18 se reparten en tres montones y no hay un cuarto:
 *
 *   - **14 se renombran** y su ruta vieja queda redirigiendo. Son las de `RUTAS_CANONICAS`.
 *   - **2 se congelan por AD-16** (`clone-studio`, `voice-avatar`): salen del nav, así que su path
 *     no se toca. Ver `RUTAS_CONGELADAS_POR_AD16`.
 *   - **2 se dejan como están con motivo escrito** (`leads`, `admin`). Ver
 *     `RUTAS_SIN_RENOMBRE_CON_MOTIVO`.
 *
 * A las 14 se le suma **1 renombre anidado** —`conversations/assignments` →
 * `conversaciones/asignacion`—, que también es un destino del nav y por eso entra: son **15
 * pares** en total.
 */

/** Un par «ruta vieja → ruta canónica». Ambas sin barra inicial, como se declaran en `path:`. */
export interface ParDeRuta {
  /** El `path` que existía antes del renombre. Nunca se borra: queda redirigiendo. */
  readonly vieja: string;
  /** El `path` canónico, en español. */
  readonly canonica: string;
}

/**
 * Los 15 pares. El orden es el de la tabla de rutas, salvo el par anidado, que va **primero** a
 * propósito: `redireccionesPermanentes()` los ordena igual por cantidad de segmentos, pero verlos
 * en ese orden acá evita que alguien «arregle» el orden y rompa el ruteo sin darse cuenta.
 *
 * Los cuatro pares que AD-14 nombra con todas las letras —`agents`/`agentes`,
 * `channels`/`canales`, `impersonation`/`impersonacion`, `knowledge-base`/`base-conocimiento`—
 * están los cuatro acá.
 */
export const RUTAS_CANONICAS: readonly ParDeRuta[] = [
  // Anidada, y destino del nav por derecho propio: «Asignación» es un ítem del menú, no una
  // pestaña de Conversaciones. Necesita su propio par porque el prefijo de la madre sólo la
  // llevaría hasta `conversaciones/assignments`.
  { vieja: 'conversations/assignments', canonica: 'conversaciones/asignacion' },

  { vieja: 'dashboard', canonica: 'panel' },
  { vieja: 'conversations', canonica: 'conversaciones' },
  { vieja: 'agents', canonica: 'agentes' },
  { vieja: 'labs', canonica: 'laboratorio' },
  { vieja: 'channels', canonica: 'canales' },
  { vieja: 'knowledge-base', canonica: 'base-conocimiento' },
  { vieja: 'integrations', canonica: 'integraciones' },
  { vieja: 'analytics', canonica: 'analitica' },
  { vieja: 'team', canonica: 'equipo' },
  { vieja: 'settings', canonica: 'configuracion' },
  { vieja: 'support', canonica: 'soporte' },
  { vieja: 'impersonation', canonica: 'impersonacion' },
  { vieja: 'notifications', canonica: 'notificaciones' },
  { vieja: 'payments', canonica: 'pagos' },
];

/**
 * Las rutas que **salen del nav** (Historia 1.8) y por eso **no se renombran**: AD-14 caso 2.
 *
 * La razón no es idiomática, es de superficie. Lo que protege a una capacidad retirada es su
 * puerta de entrada, no el idioma de su path; renombrarla agrega un redirect que nadie recorre y
 * una superficie más donde equivocarse. Se listan con el path completo porque cuatro de las seis
 * cuelgan de `admin`.
 *
 * Cada una tiene su puerta nombrada con `archivo:línea` en
 * `docs/console-v2/decisiones/02-retiro-y-reubicacion-del-nav.md`.
 */
export const RUTAS_CONGELADAS_POR_AD16: readonly string[] = [
  'clone-studio',
  'voice-avatar',
  'admin/tenants',
  'admin/consumo',
  'admin/metodo-venta',
  'admin/impersonacion',
];

/**
 * Las tres rutas que **nunca** se renombran, y no por decisión de diseño: por contrato con
 * terceros. AD-14 caso 4 nombra las dos primeras; la tercera la agrega la Historia 1.5b.
 */
export const RUTAS_EXCEPTUADAS_DEL_RENOMBRE: readonly { path: string; porQue: string }[] = [
  {
    path: 'connect/instagram/callback',
    porQue:
      'URL de redirección registrada en la app de Meta. Traducirla rompe el alta del canal de ' +
      'Instagram, y el error aparece del lado de Meta, no acá.',
  },
  {
    path: 'connect/messenger/callback',
    porQue: 'Misma razón que Instagram: está registrada en la app de Meta.',
  },
  {
    path: 'password/reset',
    porQue:
      'Está horneada en el enlace del correo que manda el backend (graph_mailer.' +
      'send_password_reset_email). Cambiarla de un solo lado rompe todos los correos ya enviados.',
  },
  {
    path: 'auth/confirmar-email',
    porQue:
      'Same reason as the reset: it is baked into the link of the email change (core H119, ' +
      'graph_mailer.send_email_change_confirmation_email). Renaming it on one side breaks every ' +
      'link already mailed.',
  },
];

/**
 * Las dos rutas en inglés del shell que se quedan como están, con el motivo escrito.
 *
 * No son excepciones «porque sí»: son los dos casos donde renombrar hace daño o no significa nada.
 */
export const RUTAS_SIN_RENOMBRE_CON_MOTIVO: readonly {
  path: string;
  porQue: string;
  /**
   * A dónde llega el router cuando la ruta tiene un hijo por defecto.
   *
   * No es lo mismo que un renombre y por eso vive acá y no en `RUTAS_CANONICAS`:
   * el `path` NO se tradujo —que es lo que AD-14 protege—, pero entrar a la
   * madre aterriza en su primera pestaña. Se declara para que el e2e siga
   * siendo una igualdad exacta: aflojarlo a un `startsWith` dejaría pasar un
   * renombre de verdad.
   */
  destinoResuelto?: string;
}[] = [
  {
    path: 'leads',
    porQue:
      '«Leads» ES la palabra del producto en castellano: es la etiqueta que el dueño del producto ' +
      'fijó para el nav («Leads / Pipeline»), la que dice la pantalla y la que usa el equipo. ' +
      'AD-14 manda que el renombre siga al nav; acá seguirlo significa no tocarla. Traducirla a ' +
      '/prospectos dejaría la URL diciendo una cosa y el menú otra.',
  },
  {
    path: 'admin',
    porQue:
      'Es la madre de cuatro rutas congeladas por AD-16 (tenants, consumo, metodo-venta, ' +
      'impersonacion). Renombrar la madre les mueve la URL efectiva a las cuatro, que es ' +
      'exactamente lo que el congelamiento existe para impedir. Y «admin» es la misma ' +
      'abreviatura en los dos idiomas: el renombre no traduce nada y rompe cuatro puertas.',
    // `admin.routes.ts` la convirtió en madre de cuatro pestañas (Historia 9.3,
    // commit `36d6fdc`) con `{ path: '', pathMatch: 'full', redirectTo: 'add-ons' }`,
    // así que el router reconoce `/admin/add-ons`. El path de `admin` sigue sin
    // renombrarse, que es lo que este archivo protege.
    destinoResuelto: 'admin/add-ons',
  },
];

/**
 * Las redirecciones permanentes listas para pegar en la tabla de rutas.
 *
 * Se ordenan por cantidad de segmentos, de más a menos, porque el router toma **el primer path
 * que matchea** y `conversations` matchearía `conversations/assignments` como prefijo. Ordenarlo
 * acá y no confiar en el orden del array es lo que hace que reordenar `RUTAS_CANONICAS` no pueda
 * romper el ruteo.
 */
export function redireccionesPermanentes(): Routes {
  return [...RUTAS_CANONICAS]
    .sort((a, b) => b.vieja.split('/').length - a.vieja.split('/').length)
    .map(({ vieja, canonica }) => ({ path: vieja, redirectTo: canonica }));
}
